import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

// components
import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import ThumbSwitch from "../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import Pagination from "../../../../../components/Pagination";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import Typography from "../../../../../components/Text";
import getAssignmentsCols, { columnGroups } from "./components/AssignmentsCols";

import { SearchServiceSort, SortDirection } from "../../../../../api/hooks";
import { useReviewAssignments } from "../../../../../api/hooks/defs/review-assignments";

// template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";
// css
import css from "./Assignments.module.scss";

export type Assignment = {
	current_author_assigned: number;
	current_author_overdue_assigned: number;
	current_signer_assigned: number;
	current_signer_overdue_assigned: number;
	lower_name_searchable: string;
	lower_name_sortable: string;
	name: string;
	reports_counts: number;
	waiting_author_assigned: number;
	waiting_author_overdue: number;
	waiting_signer_assigned: number;
	waiting_signer_overdue: number;
	_id: string;
};

type AssignmentsProps = {
	navLinks: SideNavigationLink[];
};

const Assignments = ({ navLinks }: AssignmentsProps) => {
	const { page } = useParams<{ page: string }>();

	const [showOnlyOverdue, setShowOnlyOverdue] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [currentSort, setCurrentSort] = useState<SearchServiceSort>({
		name: "lower_name_sortable",
		direction: "asc",
	});
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [searchValue] = useDebounceValue(searchTerm, 500);

	const [downloadingCSV, setDownloadingCSV] = useState(false);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchValue]);

	const { data: assignmentsData, isLoading } = useReviewAssignments(
		showOnlyOverdue,
		searchValue,
		currentSort,
		(currentPage - 1) * REVIEWS_TABLE_LIMIT,
		REVIEWS_TABLE_LIMIT
	);

	const assignments = assignmentsData?.assignments || [];
	const assignmentsCount = assignmentsData?.count[0]?.count || 0;

	const emptyTableState = <EmptySearchTableMessage mainText="No Assignments Found" />;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchTerm}
			onSearchInputChange={(value) => setSearchTerm(value)}
			activeFiltersCount={0}
			searchPlaceholder="Search employees..."
		/>
	);

	const csvFilters = `&employee_name_search=${searchTerm}${
		currentSort.name ? `&sortName=${currentSort.name}&sortDirection=${currentSort.direction}` : ""
	}`;

	const actionButtons = [
		<div className={css.onlyOverdueSwitch} key="overdue-switch">
			<Typography weight="semi-bold" size="paragraph-3">
				Show Only Overdue
			</Typography>
			<ThumbSwitch value={showOnlyOverdue} onChange={() => setShowOnlyOverdue(!showOnlyOverdue)} />
		</div>,
		<div className={css.downloadButton} key="downloadButton">
			<a href={`/company/reviews/overview.csv/?${csvFilters}`}>
				<DownloadButton
					onClick={() => setDownloadingCSV(true)}
					style={{ height: "32px" }}
					isJobRunning={downloadingCSV}
				>
					Download CSV
				</DownloadButton>
			</a>
		</div>,
	];

	const mainContent = (
		<div>
			<ScrollableTable
				columns={getAssignmentsCols()}
				columnGroups={columnGroups}
				rows={assignments}
				isLoading={isLoading}
				activeSort={currentSort.name}
				activeSortDirection={currentSort.direction}
				onColumnClick={(columnID, nextDirection) =>
					setCurrentSort({ name: columnID, direction: nextDirection as SortDirection })
				}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(assignmentsCount / REVIEWS_TABLE_LIMIT)}
				currentPage={currentPage}
				onClick={(page: number) => setCurrentPage(page)}
			/>
		</div>
	);

	return (
		<div>
			<InfoPageTemplate
				toolbarActionRow={toolbarActionRow}
				actionButtons={actionButtons}
				mainContent={mainContent}
				navigationLinks={navLinks || CompanyReviewsNavigationLinks}
				activeNavigationLinkKey={page}
			/>
		</div>
	);
};

export default Assignments;
